import { useState, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

/**
 * useBarcodeScanner hook
 *
 * Usage:
 *   const { scannerOpen, openScanner, closeScanner, handleScan, scanning } = useBarcodeScanner({ onItemFound });
 *
 * onItemFound(item) — called with item data when barcode lookup succeeds
 *   item: { item_name, description, quantity, unit, rate }
 */
export default function useBarcodeScanner({ onItemFound }) {
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanning, setScanning] = useState(false);

  const openScanner = useCallback(() => setScannerOpen(true), []);
  const closeScanner = useCallback(() => setScannerOpen(false), []);

  const handleScan = useCallback(async (code) => {
    if (scanning) return;
    setScanning(true);
    try {
      const res = await api.get(`/barcodes/lookup?code=${encodeURIComponent(code)}`);
      const items = res.data;
      if (!items || !items.length) {
        toast.error(`No item found for: ${code}`);
        setScanning(false);
        return;
      }
      // If multiple items found for same code, use first
      const item = items[0];
      const mapped = {
        item_name: item.item_name || '',
        description: item.description || (item.dimensions ? item.dimensions : '') || '',
        // Each scan = 1 unit. The full item qty is intentionally NOT pre-filled
        // here — the user adjusts the qty manually after scanning.
        quantity: '1',
        unit: item.unit || 'pcs',
        rate: item.unit_price ? String(item.unit_price) : '',
        proto_code: item.proto_code || '',
        project_item_id: item.id || null,
        _scanned: true,
        _project: item.project_name || '',
      };
      onItemFound(mapped);
      toast.success(`Added: ${item.item_name}`, { icon: '✅', duration: 2000 });
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error(`Barcode not found: ${code}`, { duration: 3000 });
      } else {
        toast.error('Lookup failed. Check connection.');
      }
    } finally {
      setScanning(false);
    }
  }, [onItemFound, scanning]);

  return { scannerOpen, openScanner, closeScanner, handleScan, scanning };
}
